/**
 * The error that actually stopped a run, pulled out of its log.
 *
 * ## Why this exists
 *
 * A failed run records `The installer exited 1: Essential container in task
 * exited.` — which is true, and tells nobody anything. The completion event is
 * an ECS task state change and carries no output, so that is genuinely all the
 * *event* knows.
 *
 * The reason is in the log, and in one real case it was ten thousand lines past
 * where anyone was looking: a plan that ran three minutes, planned eleven
 * hundred resources, and died at the last step on `could not login to OCI
 * registry "qrvey.azurecr.io": unauthorized` — because a box on the run form
 * was left unticked. Finding that took a direct CloudWatch read. It should take
 * a glance.
 *
 * The product already holds the rule this breaks: *a refusal shows the reason
 * the customer's account gave*. This is the same idea one layer down, and the
 * same technique `deployments/outputs.ts` uses for the end-of-run banner —
 * parse what the installer printed, because there is no other path to it.
 *
 * ## Why parsing is safe here
 *
 * Terraform's diagnostic frame has been stable for years and the installer does
 * not reformat it. More to the point the downside is bounded: finding nothing
 * leaves the caller exactly where it is today, with the generic message. This
 * can fail to help; it cannot mislead.
 *
 * ## What is deliberately not done
 *
 * **Nothing is stored.** This runs over a log read on demand, so there is no
 * second copy of a customer's error text in Qrvey's table to keep, expire or
 * leak. The lines it reads were redacted in the customer's own account before
 * they were written.
 */
import { stripAnsi } from './ansi.js';
/*
 * Terraform frames every diagnostic the same way, with box-drawing characters:
 * an opening U+2577, body lines prefixed U+2502, a closing U+2575.
 *
 * **Warnings use exactly the same frame**, and a real plan emits several —
 * deprecated attributes, redundant ignore_changes, invalid attribute
 * combinations. Matching the frame alone would headline a deprecation notice as
 * the reason a deployment failed, which is worse than saying nothing. So the
 * severity word is part of the match.
 */
const OPEN = '╷';
const CLOSE = '╵';
const BODY = /^\s*│\s?(.*)$/;
/** `Error: …` starts a block worth reporting. `Warning: …` does not. */
const ERROR_HEAD = /^Error:\s*(.+)$/;
const WITH = /^with\s+(.+?),?\s*$/;
const AT = /^on\s+(\S+\s+line\s+\d+)/;
/**
 * Every Terraform error block in the log, oldest first.
 *
 * All of them rather than the first, because one root cause often produces
 * several. The caller decides which to lead with; `lastFailure` is the usual
 * answer.
 */
export const failuresIn = (lines) => {
    const found = [];
    const clean = lines.map((line) => stripAnsi(line).trimEnd());
    let block;
    for (const line of clean) {
        const trimmed = line.trim();
        if (trimmed === OPEN) {
            block = [];
            continue;
        }
        if (block === undefined)
            continue;
        if (trimmed === CLOSE) {
            const parsed = parseBlock(block);
            if (parsed !== undefined)
                found.push(parsed);
            block = undefined;
            continue;
        }
        const body = BODY.exec(line);
        /*
         * A frame that never closes — the log truncated mid-block — is dropped
         * rather than reported half-read. Anything not shaped like a body line ends
         * it too: the frame does not nest, so that means the log interleaved and
         * whatever we have is not reliably one diagnostic.
         */
        if (body === null) {
            block = undefined;
            continue;
        }
        block.push(body[1] ?? '');
    }
    return found;
};
const parseBlock = (body) => {
    const first = body.find((l) => l.trim() !== '');
    if (first === undefined)
        return undefined;
    const head = ERROR_HEAD.exec(first.trim());
    // A Warning block, or something else entirely. See the note above.
    if (head === null)
        return undefined;
    const summary = (head[1] ?? '').trim();
    if (summary === '')
        return undefined;
    let resource;
    let at;
    for (const line of body) {
        const t = line.trim();
        const w = WITH.exec(t);
        if (w !== null && resource === undefined)
            resource = w[1];
        const a = AT.exec(t);
        if (a !== null && at === undefined)
            at = a[1];
    }
    return {
        summary,
        detail: body.join('\n').trim(),
        ...(resource === undefined ? {} : { resource }),
        ...(at === undefined ? {} : { at }),
    };
};
/**
 * The error to lead with: the last one Terraform printed.
 *
 * The last rather than the first because Terraform prints diagnostics as it
 * resolves the graph, and an early one is frequently a consequence of a later,
 * more specific failure reported after it. When there is only one — the common
 * case — the two rules agree.
 */
export const lastFailure = (lines) => {
    const all = failuresIn(lines);
    return all[all.length - 1];
};
/**
 * Every *distinct* failure in a log, in the order Terraform first printed them.
 *
 * The installer retries a failed apply four times, so a log holds one block per
 * error per attempt — eight blocks for two real problems, inside 25,000 lines.
 * Showing only the last one hid the error that was going to fail the *next*
 * run too: an apply died on both a refused Postgres downgrade and an EKS access
 * entry that already existed, the panel reported the downgrade, and fixing it
 * would have bought another twenty-six minutes to be told about the other
 * (IM-141, and MP-3598 for the second error itself).
 *
 * **Deduped on a fingerprint, not on the summary**, because the summary is not
 * stable across attempts: AWS puts a fresh `RequestID` *inside* the error text,
 * so four identical failures are four different strings. Comparing summaries
 * collapsed nothing at all, which the fixture caught — it is the whole reason
 * this function needs more than a `Set`.
 *
 * First occurrence wins the position, so the list reads in the order the run
 * hit them rather than newest-first. That is the opposite of `lastFailure`'s
 * rule and deliberately so: that one picks a single headline, where a later
 * diagnostic is usually the more specific; this one is the whole account.
 */
export const distinctFailures = (lines) => {
    const byFingerprint = new Map();
    for (const failure of failuresIn(lines)) {
        const key = fingerprint(failure);
        const seen = byFingerprint.get(key);
        if (seen === undefined)
            byFingerprint.set(key, { failure, attempts: 1 });
        else
            seen.attempts += 1;
    }
    return [...byFingerprint.values()].map(({ failure, attempts }) => ({ ...failure, attempts }));
};
/**
 * What makes two attempts at the same failure the same failure.
 *
 * The identifiers AWS regenerates per call are removed and nothing else is: a
 * request id and a uuid say *which call* failed, never *what* failed. Anything
 * beyond that risks collapsing two real problems into one, which is the error
 * this function exists to prevent — so the rule stays narrow and the resource
 * and location stay in the key.
 */
const fingerprint = (failure) => [
    failure.summary
        .replace(/RequestID: \S+/gi, 'RequestID: …')
        .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '…'),
    failure.resource ?? '',
    failure.at ?? '',
].join('\u0000');
/** The installer's timestamp and level, when a line carries them. */
const INSTALLER_PREFIX = /^\d{4}-\d{2}-\d{2}T\S+\s+[A-Za-z]+\s+/;
const VERDICT_HEAD = /^Post-apply verification did not pass \(exit (\d+)\):$/;
const VERDICT_REASON = /^-\s+(\S.*)$/;
const messageOf = (line) => stripAnsi(line).replace(INSTALLER_PREFIX, '').trim();
/**
 * The last verdict block in the log, or nothing.
 *
 * The last, because a verdict is printed once per run and at its end; a second
 * one in a log is a re-run inside the same task, and the later is the answer.
 * A header with no reason under it is dropped rather than reported half-read —
 * the same rule as an unclosed Terraform frame — because the reasons are the
 * part anyone acts on, and a page boundary can fall between the two.
 */
export const installerVerdict = (lines) => {
    let found;
    let open;
    const close = () => {
        if (open !== undefined && open.reasons.length > 0) {
            found = {
                exitCode: open.exitCode,
                summary: `Post-apply verification did not pass (exit ${String(open.exitCode)})`,
                reasons: open.reasons,
            };
        }
        open = undefined;
    };
    for (const line of lines) {
        const message = messageOf(line);
        const head = VERDICT_HEAD.exec(message);
        if (head !== null) {
            close();
            open = { exitCode: Number(head[1]), reasons: [] };
            continue;
        }
        if (open === undefined)
            continue;
        const reason = VERDICT_REASON.exec(message);
        if (reason === null) {
            close();
            continue;
        }
        open.reasons.push(reason[1] ?? '');
    }
    close();
    return found;
};
/**
 * Which part of a verdict block a line is, if any — a cheap test, so the log
 * viewer can keep the handful of lines `installerVerdict` needs without holding
 * or re-reading the whole log. A `reason` only counts after a `head`: plenty of
 * Terraform output also starts with a dash.
 */
export const verdictLineKind = (line) => {
    const message = messageOf(line);
    if (VERDICT_HEAD.test(message))
        return 'head';
    if (VERDICT_REASON.test(message))
        return 'reason';
    return undefined;
};
//# sourceMappingURL=failure-extract.js.map